import React, { useMemo } from 'react';
import { DatePicker, ConfigProvider, theme } from 'antd';
import dayjs from 'dayjs';
import { useTheme } from '../../contexts/ThemeContext';

const { RangePicker: AntRangePicker } = DatePicker;

/**
 * Isolated Ant Design RangePicker Component
 * Does not affect existing code - use only where needed
 */
const AntRangePickerComponent = ({ 
  value, 
  onChange, 
  placeholder = ['Start date', 'End date'],
  format = 'YYYY-MM-DD',
  disabled = false,
  className = '',
  style = {},
  showPresets = true,
  ...props 
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const themeConfig = useMemo(() => ({
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: '#3b82f6',
      borderRadius: 12,
      colorBgContainer: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
      colorBgElevated: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
      colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.12)',
      colorSplit: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(203, 213, 225, 0.6)',
      colorText: isDark ? '#ffffff' : '#0f172a',
      colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(100, 116, 139, 0.6)',
      colorTextQuaternary: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(148, 163, 184, 0.6)',
      colorTextTertiary: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(71, 85, 105, 0.75)',
      colorTextSecondary: isDark ? 'rgba(255, 255, 255, 0.8)' : '#1f2937',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: 13,
      controlHeight: 40,
      controlPaddingHorizontal: 12,
    },
    components: {
      DatePicker: {
        cellHoverBg: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.12)',
        cellActiveWithRangeBg: isDark ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.18)',
        colorBgElevated: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
        colorText: isDark ? '#ffffff' : '#0f172a',
        colorTextHeading: isDark ? '#ffffff' : '#0f172a',
        colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(100, 116, 139, 0.6)',
        colorTextDisabled: isDark ? 'rgba(148, 163, 184, 0.4)' : 'rgba(148, 163, 184, 0.7)',
        colorFillAlter: isDark ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
        controlPaddingHorizontal: 12,
      },
    },
  }), [isDark]);

  // Default presets
  const presets = showPresets ? [
    { label: 'Today', value: [dayjs(), dayjs()] },
    { label: 'Yesterday', value: [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')] },
    { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day'), dayjs()] },
    { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
    { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  ] : [];

  return (
    <ConfigProvider theme={themeConfig}>
      <AntRangePicker
        value={value ? [dayjs(value[0]), dayjs(value[1])] : null}
        onChange={(dates, dateStrings) => {
          if (onChange) {
            onChange(
              dates ? [dates[0]?.toDate(), dates[1]?.toDate()] : null,
              dateStrings
            );
          }
        }}
        placeholder={placeholder}
        format={format}
        disabled={disabled}
        presets={presets}
        className={`ant-range-picker-custom ${className}`}
        style={{ width: '100%', ...style }}
        {...props}
      />
    </ConfigProvider>
  );
};

export default AntRangePickerComponent;
export { dayjs };
